import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import moment from 'moment'
import { ToolTip, Div, H2 } from '@driftt/tide-core';
import { Table } from '@driftt/tide-table'
import { CHART_NO_DATA_TEXT, signatureConversationColumns, conversationColumns } from 'constants/ReportConstants'
import GraphContainer from 'components/Reports/EmailPerformance/Graphs'
import RowFormatter from 'components/Reports/EmailPerformance/TableRowFormatter'
import { EmptyGraphAndPage } from 'components/Reports/EmailPerformance/EmptyState'
import { roundPercentValue } from 'components/Reports/EmailPerformance/utils'
import { get } from 'lodash'

class TotalConversations extends Component {
  constructor(props) {
    super(props)

    this.state = {
      currentData: null
    }
  }

  componentDidMount() {
    this.setState({
      currentData: this.generateGraphData()
    })
  }

  generateTableRow = (email, index) => {
    const { data } = this.props

    const emailCampaignReport =  data.byEmail.find((mail) => mail.campaignId === email.campaignId)

    return RowFormatter.ConversationRows(emailCampaignReport, email.title, index)
  }

  generateGraphData() {
    const { data } = this.props
    const xAxisBuckets = []
    const colors = ['hsl(187, 62%, 64%)', 'hsl(255, 62%, 65%)', 'hsl(201, 62%, 63%)']
    const convos = {
      name: 'Chat',
      data: [],
      color: colors[0]
    }
    const meetings = {
      name: 'Meetings',
      data: [],
      color: colors[1]
    }
    const replies = {
      name: 'Replies',
      data: [],
      color: colors[2]
    }

    data.byDate.current.forEach((date) => {
      xAxisBuckets.push(moment.utc(date.date).valueOf())
      const convoDatum = get(date, 'metrics.CONVERSATION_INFLUENCED.value', 0)
      const meetingsDatum = get(date, 'metrics.MEETING_INFLUENCED.value', 0)
      const repliesDatum = get(date, 'metrics.REPLIED.value', 0)

      convos.data.push(convoDatum)
      meetings.data.push(meetingsDatum)
      replies.data.push(repliesDatum)
    })

    const stackedValues = [replies, convos, meetings].filter(series => series.data.length > 0)

    return {
      xAxisBuckets,
      stackedValues
    }
  }

  generateSignatureRows = (userData) => {
    const { data, orgUsers } = this.props

    // change [{ id: 1, name: 'jacob', ...}, { id: 2, name: 'foster', ...}]
    // into:
    // { 1: { id: 1, name: 'jacob', ...}, 2: { id: 2, name: 'foster', ... }}
    const userIdToUserMap = {}
    orgUsers.forEach(user => userIdToUserMap[user.id] = user)

    return !!data ? data.byUser
      .map(userData => ({ user: userIdToUserMap[userData.userId], metrics: userData.metrics}))
      .filter(userData => !!userData.user)
      .map(RowFormatter.SignatureConversationRows) : []
  }

  getBaseMetric(sent, delivered, opened) {
    const { playbookReportType, playbook: { reportType } = {} } = this.props
    const isSignaturePlaybook = playbookReportType === 'signature'

    return isSignaturePlaybook ? opened : (reportType === 'OUTBOUND_SEQUENCE' ? sent : delivered)
  }

  renderTable() {
    const { data, playbook, playbookReportType } = this.props
    const { actions } = playbook
    const { action } = actions[0]
    const emailCampaigns = action.emails ? action.emails : [{...action}]
    const isSignaturePlaybook = playbookReportType === 'signature'

    const rows = !data ?
      []
      :
      isSignaturePlaybook ?
        this.generateSignatureRows()
        :
        emailCampaigns.map(this.generateTableRow)

    const columns = isSignaturePlaybook ?
      signatureConversationColumns
      :
      conversationColumns
    return <Table columns={columns} className="email-reports-table" data={rows} />
  }

  renderCalloutHeader(value, title, tooltipComponent, conversionRate) {
    if (value === undefined)
      return null

    return (
      <ToolTip component={tooltipComponent} flow="up">
        <Div className="reports-data-callout-item no-pointer">
          <Div className="callout-header">{title}</Div>
          <Div className="callout-large">{value}</Div>
          <Div className="callout-subtext">{conversionRate}</Div>
        </Div>
      </ToolTip>
    )
  }

  render() {
    const { activeState, data } = this.props
    const { currentData } = this.state

    const total = data.total

    const deliveredValue = get(total, 'DELIVERED.value')
    const openedValue = get(total, 'OPENED.value')
    const sentValue = get(total, 'SENT.value')
    const repliedValue = get(total, 'REPLIED.value')
    const conversationValue = get(total, 'CONVERSATION_INFLUENCED.value')
    const meetingBookedValue = get(total, 'MEETING_INFLUENCED.value')

    const baseMetric = this.getBaseMetric(sentValue, deliveredValue, openedValue)

    const repliedPercentValue = roundPercentValue(repliedValue, baseMetric)
    const websiteChatPercentValue = roundPercentValue(conversationValue, baseMetric)
    const meetingsBookedPercentValue = roundPercentValue(meetingBookedValue, baseMetric)

    // TODO: may have to revisit this
    const hasDisplayData = !(
      (
        meetingBookedValue === 0 ||
        meetingBookedValue === undefined
      ) &&
      (
        conversationValue === 0 ||
        conversationValue === undefined
      ) &&
      (
        repliedValue === 0 ||
        repliedValue === undefined
      )
    )

    return (
      <Div className="email-performance-breakdown email-performance-breakdown--conversations">
        {hasDisplayData ?
          <Div className="reports-data-callout">
            {this.renderCalloutHeader(repliedValue, 'Email replies',
              <Div className="metric-tooltip-center"> The number of people who <br/>replied to the email<br/></Div>,
              `(${repliedPercentValue}%)`,
            )}
            {this.renderCalloutHeader(conversationValue, 'Website chats',
              <Div className="metric-tooltip-center">Total number of people who<br />clicked through an email and<br />started a chat on your website.</Div>,
              `(${websiteChatPercentValue}%)`,
            )}
            {this.renderCalloutHeader(meetingBookedValue, 'Meetings booked',
              <Div className="metric-tooltip-center">Total number of people who<br />clicked through an email and<br />booked a meeting.</Div>,
              `(${meetingsBookedPercentValue}%)`,
            )}
          </Div> :
          <Fragment>
            <Div className="reports-graph-data-overlay">
              <EmptyGraphAndPage />
              <H2>{CHART_NO_DATA_TEXT}</H2>
            </Div>
          </Fragment>
        }
        <Div className="reports-graph-container">
          {!!currentData &&
            <GraphContainer activeTab={activeState} graphData={currentData} />
          }
        </Div>
        <Div className="reports-table-container">
          {this.renderTable()}
        </Div>
      </Div>
    )
  }
}

const mapStateToProps = state => ({
  orgUsers: state.org.users,
})

export default connect(mapStateToProps)(TotalConversations)
